import Dialog from '@app/src/components/Dialog'
import { NAV_ITEM_HEIGHT, buttonResetSx } from '@app/src/components/Navigation/VerticalMenu/navItemStyles'
import { useVerticalMenu } from '@app/src/components/Navigation/VerticalMenu/VerticalMenuContext'
import TextField from '@app/src/components/Ui/TextField'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import { useDialogState, useMenuState } from '@app/src/hooks/mui-hooks'
import useIsAccessorSolution from '@app/src/hooks/useIsAccessorSolution'
import {
  hasAnyMrtPageEnabled,
  isPageKeyEnabled,
  routeForPageKey,
} from '@app/src/pages/ResourceCollection/MrtCollection/mrtPages'
import { groupViews } from '@app/src/pages/ResourceCollection/MrtCollection/savedViews/savedViewConfig'
import {
  useAllSavedViews,
  useDeleteSavedView,
  useUpdateSavedView,
} from '@app/src/pages/ResourceCollection/MrtCollection/savedViews/savedViewHooks'
import type { SavedView } from '@app/src/types/savedViews'
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Box, Collapse, Divider, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'

const rowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minHeight: NAV_ITEM_HEIGHT,
  pr: 1,
  borderRadius: 2,
  color: 'text.secondary',
  '&:hover': { color: 'text.primary', backgroundColor: 'action.hover' },
}

// The row container owns the background/hover; the link is layout-only so the row doesn't paint a
// grey box inside a grey box on hover.
const rowLinkSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flex: 1,
  minWidth: 0,
  pl: 3,
  textDecoration: 'none',
  color: 'inherit',
}

type SavedViewRowProps = { view: SavedView; canManage: boolean }

const SavedViewRow: React.FC<SavedViewRowProps> = ({ view, canManage }) => {
  const { formatMessage } = useIntl()
  const [isMenuOpen, menuAnchor, openMenu, closeMenu] = useMenuState()
  const [isRenameOpen, openRename, closeRename] = useDialogState()
  const [isDeleteOpen, openDelete, closeDelete] = useDialogState()
  const [name, setName] = useState(view.name)

  const updateSavedView = useUpdateSavedView()
  const deleteSavedView = useDeleteSavedView()

  const route = routeForPageKey(view.pageKey)
  if (!route) return null

  const to = `${route}?view=${view.id}`

  const handleRename = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    updateSavedView.mutate(
      {
        id: view.id,
        body: { name: name.trim(), pageKey: view.pageKey, visibility: view.visibility, config: view.config },
      },
      { onSuccess: closeRename },
    )
  }

  const handleDelete = (): void => {
    deleteSavedView.mutate(view.id, { onSuccess: closeDelete })
  }

  return (
    <>
      <Box sx={rowSx}>
        <Box component={Link} to={to} sx={rowLinkSx}>
          <BookmarkBorderOutlinedIcon fontSize="small" />
          <Typography variant="subtitle2" color="inherit" noWrap sx={{ flexGrow: 1 }}>
            {view.name}
          </Typography>
        </Box>
        {canManage && (
          <IconButton
            size="small"
            onClick={openMenu}
            aria-label={formatMessage({ id: 'savedViews.manageView' }, { name: view.name })}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {canManage && (
        <Menu anchorEl={menuAnchor} open={isMenuOpen} onClose={closeMenu}>
          <MenuItem
            onClick={() => {
              setName(view.name)
              openRename()
              closeMenu()
            }}
          >
            {formatMessage({ id: 'savedViews.rename' })}
          </MenuItem>
          <MenuItem
            onClick={() => {
              openDelete()
              closeMenu()
            }}
          >
            {formatMessage({ id: 'general.delete' })}
          </MenuItem>
        </Menu>
      )}

      <Dialog
        open={isRenameOpen}
        onClose={closeRename}
        onFormSubmit={handleRename}
        title={formatMessage({ id: 'savedViews.renameTitle' })}
        content={
          <TextField
            name="saved-view-rename"
            label={formatMessage({ id: 'general.name' })}
            value={name}
            onChange={event => setName(event.target.value)}
            fullWidth
            autoFocus
          />
        }
        buttons={[
          {
            onClick: closeRename,
            label: formatMessage({ id: 'general.cancel' }),
            disabled: updateSavedView.isPending,
          },
          {
            type: 'submit',
            variant: 'contained',
            label: formatMessage({ id: 'general.save' }),
            disabled: !name.trim() || updateSavedView.isPending,
            loading: updateSavedView.isPending,
          },
        ]}
      />

      <Dialog
        open={isDeleteOpen}
        onClose={closeDelete}
        title={formatMessage({ id: 'savedViews.deleteTitle' })}
        content={formatMessage({ id: 'savedViews.deleteConfirm' }, { name: view.name })}
        buttons={[
          {
            onClick: closeDelete,
            label: formatMessage({ id: 'general.cancel' }),
            disabled: deleteSavedView.isPending,
          },
          {
            onClick: handleDelete,
            variant: 'contained',
            color: 'error',
            label: formatMessage({ id: 'general.delete' }),
            disabled: deleteSavedView.isPending,
            loading: deleteSavedView.isPending,
          },
        ]}
      />
    </>
  )
}

type SavedViewSectionProps = { titleIntlId: string; views: SavedView[]; currentUserId: number | null }

const SavedViewSection: React.FC<SavedViewSectionProps> = ({ titleIntlId, views, currentUserId }) => {
  const { formatMessage } = useIntl()
  const [open, setOpen] = useState(false)
  const regionId = `saved-views-section-${titleIntlId}`

  if (!views.length) return null

  return (
    <Box>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen(previous => !previous)}
        aria-expanded={open}
        aria-controls={regionId}
        sx={[buttonResetSx, rowSx, { px: 1.5 }]}
      >
        <FolderOpenOutlinedIcon fontSize="small" />
        <Typography variant="subtitle2" color="inherit" noWrap sx={{ flexGrow: 1 }}>
          {formatMessage({ id: titleIntlId })}
        </Typography>
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>
      <Collapse in={open} id={regionId}>
        <Stack>
          {views.map(view => (
            <SavedViewRow key={view.id} view={view} canManage={currentUserId === view.creatorUserId} />
          ))}
        </Stack>
      </Collapse>
    </Box>
  )
}

const SavedViewsMenu = (): JSX.Element | null => {
  const { newProductMappingUi = false, newTablesWithMaterialReactTable = false, newSupplierRiskUi = false } = useFlags()
  const isAccessorSolution = useIsAccessorSolution()
  const { isExpanded } = useVerticalMenu()
  const { userId } = useAuthentication()
  const mrtPageFlags = { newProductMappingUi, newTablesWithMaterialReactTable, newSupplierRiskUi }
  const isEnabled = hasAnyMrtPageEnabled(mrtPageFlags) && isAccessorSolution
  const { data: views = [] } = useAllSavedViews({ enabled: isEnabled })

  // The sections are inline lists that only make sense in the expanded rail; hidden when collapsed.
  if (!isEnabled || !isExpanded) return null

  // The two rollouts move independently, so a view can belong to a page that is still on the legacy
  // table - it would link somewhere that cannot apply it.
  const grouped = groupViews(views.filter(view => isPageKeyEnabled(view.pageKey, mrtPageFlags)))
  if (!grouped.private.length && !grouped.shared.length) return null

  return (
    <Stack mt={1}>
      <Divider sx={{ mb: 1 }} />
      <SavedViewSection titleIntlId="savedViews.privateViews" views={grouped.private} currentUserId={userId} />
      <SavedViewSection titleIntlId="savedViews.sharedViews" views={grouped.shared} currentUserId={userId} />
    </Stack>
  )
}

export default SavedViewsMenu
